import { t } from "@lingui/macro";
import { ErrorMessage } from "@reactive-resume/utils";

export const translateError = (error: ErrorMessage) => {
  switch (error) {
    case ErrorMessage.InvalidCredentials:
      return t`That email, username, or password doesn't match. Please try again.`;
    case ErrorMessage.InvalidCurrentPassword:
      return t`That current password doesn't match. Please try again.`;
    case ErrorMessage.UserAlreadyExists:
      return t`An account already uses that email or username.`;
    case ErrorMessage.SecretsNotFound:
      return t`User does not have an associated 'secrets' record. Please report this issue on GitHub.`;
    case ErrorMessage.OAuthUser:
      return t`This account uses Google or GitHub. Sign in with that button instead of a password.`;
    case ErrorMessage.InvalidResetToken:
      return t`That reset link doesn't work anymore. Request a new one and try again.`;
    case ErrorMessage.InvalidVerificationToken:
      return t`It looks like the verification token you provided is invalid. Please try restarting the verification process again.`;
    case ErrorMessage.InvalidEmailChangeToken:
      return t`That email change link is invalid or expired. Request a new one from Settings.`;
    case ErrorMessage.EmailAlreadyVerified:
      return t`It looks like your email address has already been verified.`;
    case ErrorMessage.TwoFactorNotEnabled:
      return t`Two-factor authentication is not enabled for this account.`;
    case ErrorMessage.TwoFactorAlreadyEnabled:
      return t`Two-factor authentication is already enabled for this account.`;
    case ErrorMessage.InvalidTwoFactorCode:
      return t`That code doesn't match. Please try again.`;
    case ErrorMessage.InvalidTwoFactorBackupCode:
      return t`That backup code doesn't match, or it was already used.`;
    case ErrorMessage.InvalidBrowserConnection:
      return t`There was an error connecting to the browser. Please make sure 'chrome' is running and reachable.`;
    case ErrorMessage.ResumeSlugAlreadyExists:
      return t`A resume with this slug already exists, please pick a different unique identifier.`;
    case ErrorMessage.ResumeNotFound:
      return t`It looks like the resume you're looking for doesn't exist.`;
    case ErrorMessage.ResumeLocked:
      return t`The resume you want to update is locked, please unlock if you wish to make any changes to it.`;
    case ErrorMessage.ResumePrinterError:
      return t`Something went wrong while printing your resume. Please try again later or raise an issue on GitHub.`;
    case ErrorMessage.ResumePreviewError:
      return t`Something went wrong while grabbing a preview your resume. Please try again later or raise an issue on GitHub.`;
    case ErrorMessage.SomethingWentWrong:
      return t`Something went wrong while processing your request. Please try again later or raise an issue on GitHub.`;

    default:
      return null;
  }
};

type ValidationIssue = {
  message?: unknown;
  path?: unknown;
};

const technicalValidation =
  /^(required|invalid|invalid email|invalid email address|invalid input|expected|string must|number must|too small|too big)\b/i;

const isSpokenMessage = (detail: string) => {
  if (detail.length > 200) return false;
  if (/[{}[\]`]/.test(detail)) return false;
  if (technicalValidation.test(detail)) return false;
  return /[.!?]/.test(detail);
};

export const naturalValidationMessage = (errors: ValidationIssue[] | undefined): string => {
  const issue = errors?.find((item) => typeof item.message === "string" && item.message.trim());
  const detail = typeof issue?.message === "string" ? issue.message.trim() : "";
  const path = Array.isArray(issue?.path) ? issue.path.map(String) : [];

  if (detail && isSpokenMessage(detail)) return detail;

  if (path.includes("email") || path.includes("identifier")) {
    return t`Please check the email address and try again.`;
  }
  if (path.includes("password")) {
    return t`Please check the password and try again.`;
  }
  if (path.includes("username")) {
    return t`Please check the username and try again.`;
  }
  if (path.includes("code")) {
    return t`Please check the code and try again.`;
  }
  return t`Please check what you entered and try again.`;
};

export const messageFromServerError = (data: unknown): string | null => {
  if (!data || typeof data !== "object") return null;

  const body = data as { message?: unknown; errors?: ValidationIssue[] };
  const message = typeof body.message === "string" ? body.message : "";
  const known = translateError(message as ErrorMessage);
  if (known) return known;
  if (message !== "Validation failed") return null;

  return naturalValidationMessage(body.errors);
};
